import { useState, useEffect, useRef } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Music, 
  Upload, 
  Search, 
  Trash2, 
  Plus, 
  FileMusic, 
  RefreshCw,
  Database,
  Menu,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  Disc,
  AlertTriangle,
  CheckCircle2,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MusicLibraryPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [songs, setSongs] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_source: {} });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadMode, setUploadMode] = useState('append');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Add song state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newSong, setNewSong] = useState({ title: '', artist: '', duration: '', album: '', genre: '' });
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [songToDelete, setSongToDelete] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  useEffect(() => {
    fetchSongs();
    fetchStats();
  }, [page, searchQuery]);

  const fetchSongs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), limit: '50' });
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/music-library?${params}`);
      setSongs(response.data.songs || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch music library:', error);
      toast.error('Failed to load music library');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/music-library/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // State for upload progress
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState(null);

  // Poll for processing status
  const pollProcessingStatus = async (uploadId) => {
    try {
      const response = await axios.get(`${API}/music-library/upload-status/${uploadId}`);
      const data = response.data;
      
      console.log('[MusicLibrary] Poll status:', data);
      setProcessingStatus(data);
      
      if (data.status === 'complete') {
        setUploadResult(data);
        toast.success(`Imported ${data.imported} songs!`);
        fetchSongs();
        fetchStats();
        setUploading(false);
        setProcessingStatus(null);
        return true; // Done
      } else if (data.status === 'error') {
        console.error('[MusicLibrary] Processing error:', data.error);
        toast.error(data.error || 'Failed to process file');
        setUploadResult({ error: data.error });
        setUploading(false);
        setProcessingStatus(null);
        return true; // Done (with error)
      } else {
        // Still processing, continue polling
        return false;
      }
    } catch (error) {
      console.error('[MusicLibrary] Failed to get processing status:', error);
      // On 404, the session might have been cleaned up - treat as error
      if (error.response?.status === 404) {
        toast.error('Upload session expired or not found. Please try again.');
        setUploading(false);
        setProcessingStatus(null);
        return true; // Stop polling
      }
      // Don't stop polling on network error, might be temporary
      return false;
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validExtensions = ['.xml', '.csv', '.m3u', '.m3u8'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(ext)) {
      toast.error('Invalid file type. Please upload XML, CSV, or M3U files.');
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    setUploadProgress(0);
    setProcessingStatus({ status: 'reading', progress_message: 'Reading file...' });
    
    try {
      // For XML files, parse on client side to avoid server timeout
      if (ext === '.xml') {
        toast.info('Parsing XML file in browser...');
        
        // Read file as text
        const fileContent = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsText(file);
        });
        
        setProcessingStatus({ status: 'parsing', progress_message: 'Parsing XML...' });
        
        // Parse XML in browser
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(fileContent, 'text/xml');
        
        // Check for parse errors
        const parseError = xmlDoc.querySelector('parsererror');
        if (parseError) {
          throw new Error('Invalid XML file format');
        }
        
        // Extract songs from XML
        const songElements = xmlDoc.querySelectorAll('Song');
        const songs = [];
        
        setProcessingStatus({ status: 'extracting', progress_message: `Found ${songElements.length} songs, extracting data...` });
        
        for (let i = 0; i < songElements.length; i++) {
          const elem = songElements[i];
          const filePath = elem.getAttribute('FilePath') || '';
          const tags = elem.querySelector('Tags');
          const infos = elem.querySelector('Infos');
          
          let title = '';
          let artist = '';
          let album = '';
          let genre = '';
          let bpm = null;
          let durationSecs = null;
          let durationFmt = null;
          
          if (tags) {
            title = tags.getAttribute('Title') || tags.getAttribute('title') || '';
            artist = tags.getAttribute('Author') || tags.getAttribute('Artist') || '';
            album = tags.getAttribute('Album') || '';
            genre = tags.getAttribute('Genre') || '';
            const bpmRaw = tags.getAttribute('Bpm') || tags.getAttribute('BPM') || '';
            if (bpmRaw) {
              const parsed = parseFloat(bpmRaw);
              if (!isNaN(parsed)) bpm = parsed;
            }
          }
          
          if (!title) title = elem.getAttribute('Title') || '';
          if (!artist) artist = elem.getAttribute('Artist') || elem.getAttribute('Author') || '';
          
          if (infos) {
            const songLength = infos.getAttribute('SongLength') || '';
            if (songLength) {
              const secs = parseInt(parseFloat(songLength));
              if (!isNaN(secs)) {
                durationSecs = secs;
                durationFmt = `${Math.floor(secs / 60)}:${(secs % 60).toString().padStart(2, '0')}`;
              }
            }
          }
          
          // Extract from filename if needed
          if (!title && !artist && filePath) {
            const fileName = filePath.split(/[/\\]/).pop().replace(/\.[^/.]+$/, '');
            if (fileName.includes(' - ')) {
              const parts = fileName.split(' - ');
              artist = parts[0].trim();
              title = parts.slice(1).join(' - ').trim();
            } else {
              title = fileName.trim();
            }
          }
          
          if (title || artist) {
            songs.push({
              title: title.trim(),
              artist: artist.trim(),
              album: album.trim(),
              genre: genre.trim(),
              bpm,
              duration: durationSecs,
              duration_formatted: durationFmt,
              file_path: filePath
            });
          }
          
          // Update progress every 5000 songs
          if ((i + 1) % 5000 === 0) {
            setProcessingStatus({ status: 'extracting', progress_message: `Extracted ${i + 1} of ${songElements.length} songs...` });
            // Allow UI to update
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }
        
        toast.success(`Parsed ${songs.length} songs from XML`);
        setProcessingStatus({ status: 'uploading', progress_message: `Uploading ${songs.length} songs to server...` });
        
        // Send songs to server in batches
        const BATCH_SIZE = 2000;
        let importedCount = 0;
        
        // If replace mode, clear library first
        if (uploadMode === 'replace') {
          await axios.delete(`${API}/music-library`);
        }
        
        for (let i = 0; i < songs.length; i += BATCH_SIZE) {
          const batch = songs.slice(i, i + BATCH_SIZE);
          
          await axios.post(`${API}/music-library/import-batch`, {
            songs: batch,
            mode: uploadMode
          });
          
          importedCount += batch.length;
          setUploadProgress(Math.round((importedCount / songs.length) * 100));
          setProcessingStatus({ 
            status: 'importing', 
            progress_message: `Imported ${importedCount} of ${songs.length} songs...` 
          });
        }
        
        // Done!
        setUploadResult({
          imported: importedCount,
          total_in_library: importedCount
        });
        toast.success(`Successfully imported ${importedCount} songs!`);
        fetchSongs();
        fetchStats();
        setUploading(false);
        setProcessingStatus(null);
        return;
      }
      
      // For non-XML files, use the original chunked upload approach
      const CHUNK_SIZE = 512 * 1024; // 512KB chunks
      const useChunked = file.size > 1024 * 1024; // Use chunked for files > 1MB
    
      if (useChunked) {
        // Chunked upload for large files
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        let uploadId = '';
        
        toast.info(`Uploading large file (${(file.size / 1024 / 1024).toFixed(1)}MB) in ${totalChunks} chunks...`);
        
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          
          // Convert chunk to base64
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64String = reader.result.split(',')[1];
              resolve(base64String);
            };
            reader.readAsDataURL(chunk);
          });
          
          const response = await axios.post(`${API}/music-library/upload-chunk`, {
            chunk: base64,
            chunk_index: i,
            total_chunks: totalChunks,
            filename: file.name,
            upload_id: uploadId,
            mode: uploadMode
          });
          
          uploadId = response.data.upload_id;
          setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
          
          // Check if ready for batch processing (all chunks uploaded)
          if (response.data.ready_to_process) {
            toast.info('Upload complete! Processing your music library...');
            setProcessingStatus({ status: 'processing', progress_message: 'Starting processing...' });
            
            // Start batch processing loop
            const processBatches = async () => {
              let continueProcessing = true;
              while (continueProcessing) {
                try {
                  const batchResponse = await axios.post(`${API}/music-library/process-batch/${uploadId}`);
                  const data = batchResponse.data;
                  
                  console.log('[MusicLibrary] Batch result:', data);
                  setProcessingStatus({
                    status: data.status,
                    progress_message: data.progress_message || `Imported ${data.imported_count || 0} songs...`
                  });
                  
                  if (data.status === 'complete') {
                    setUploadResult({
                      imported: data.imported,
                      total_in_library: data.total_in_library
                    });
                    toast.success(`Imported ${data.imported} songs!`);
                    fetchSongs();
                    fetchStats();
                    setUploading(false);
                    setProcessingStatus(null);
                    continueProcessing = false;
                  } else if (data.status === 'error') {
                    toast.error(data.error || 'Processing failed');
                    setUploadResult({ error: data.error });
                    setUploading(false);
                    setProcessingStatus(null);
                    continueProcessing = false;
                  } else if (!data.continue) {
                    continueProcessing = false;
                  }
                  // Small delay between batches to not overwhelm the server
                  if (continueProcessing) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                  }
                } catch (error) {
                  console.error('[MusicLibrary] Batch processing error:', error);
                  console.error('[MusicLibrary] Error response:', error.response?.data);
                  const errorMsg = error.response?.data?.error || error.response?.data?.detail || error.message || 'Processing failed';
                  toast.error(errorMsg);
                  setUploadResult({ error: errorMsg });
                  setUploading(false);
                  setProcessingStatus(null);
                  continueProcessing = false;
                }
              }
            };
            
            processBatches();
            return; // Exit the upload loop, batch processing will handle the rest
          }
          
          // Legacy: Check if processing started via background task
          if (response.data.processing) {
            toast.info('Upload complete! Processing your music library...');
            setProcessingStatus({ status: 'processing', progress_message: 'Starting processing...' });
            
            // Start polling for status
            const pollInterval = setInterval(async () => {
              const done = await pollProcessingStatus(uploadId);
              if (done) {
                clearInterval(pollInterval);
              }
            }, 2000); // Poll every 2 seconds
            
            return; // Exit the upload loop, polling will handle the rest
          }
          
          // Check if complete (for small files that finish synchronously)
          if (response.data.complete) {
            setUploadResult(response.data);
            toast.success(`Imported ${response.data.imported} songs!`);
            fetchSongs();
            fetchStats();
            break;
          }
        }
      } else {
        // Regular upload for small files
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mode', uploadMode);
        
        const response = await axios.post(`${API}/music-library/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setUploadProgress(progress);
          }
        });
        
        setUploadResult(response.data);
        toast.success(`Imported ${response.data.imported} songs!`);
        fetchSongs();
        fetchStats();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file. Try a smaller file or check the format.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddSong = async () => {
    if (!newSong.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    try {
      await axios.post(`${API}/music-library/add`, newSong);
      toast.success('Song added to library');
      setShowAddDialog(false);
      setNewSong({ title: '', artist: '', duration: '', album: '', genre: '' });
      fetchSongs();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add song');
    }
  };

  const handleDeleteSong = async () => {
    if (!songToDelete) return;
    
    try {
      await axios.delete(`${API}/music-library/${songToDelete.id}`);
      toast.success('Song removed from library');
      setSongToDelete(null);
      setShowDeleteConfirm(false);
      fetchSongs();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete song');
    }
  };

  const handleClearLibrary = async () => {
    try {
      await axios.delete(`${API}/music-library`);
      toast.success('Music library cleared');
      setShowClearConfirm(false);
      fetchSongs();
      fetchStats();
    } catch (error) {
      toast.error('Failed to clear library');
    }
  };

  const getSourceBadgeColor = (source) => {
    switch (source) {
      case 'virtual_dj': return 'bg-purple-500';
      case 'csv': return 'bg-blue-500';
      case 'm3u': return 'bg-green-500';
      case 'manual': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="music-library-page">
      {/* Mobile sidebar toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-toggle"
      >
        <Menu className="h-6 w-6" />
      </Button>

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0 transition-transform duration-200`}>
        <Sidebar 
          user={user} 
          onLogout={onLogout} 
          isEmployee={isEmployee} 
          permissions={permissions} 
          employeeInfo={employeeInfo}
          onNavigate={() => setSidebarOpen(false)}
        />
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 p-4 lg:p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold font-heading text-foreground flex items-center gap-3" data-testid="page-title">
                <Music className="w-8 h-8 text-neon-purple" />
                Music Library
              </h1>
              <p className="text-muted-foreground mt-1">Manage your music collection for client searches</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowUploadDialog(true)}
                className="bg-neon-purple hover:bg-neon-purple/80"
                data-testid="upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Library
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowAddDialog(true)}
                data-testid="add-song-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Song
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="glass-card border-glass-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Database className="w-8 h-8 text-neon-blue" />
                  <div>
                    <p className="text-2xl font-bold">{stats.total.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total Songs</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card border-glass-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <FileMusic className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold">{stats.by_source?.virtual_dj || 0}</p>
                    <p className="text-xs text-muted-foreground">From Virtual DJ</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card border-glass-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Disc className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{(stats.by_source?.csv || 0) + (stats.by_source?.m3u || 0)}</p>
                    <p className="text-xs text-muted-foreground">From CSV/M3U</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card border-glass-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Plus className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-2xl font-bold">{stats.by_source?.manual || 0}</p>
                    <p className="text-xs text-muted-foreground">Manually Added</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search and Actions */}
          <Card className="glass-card border-glass-border mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative flex-1 w-full md:max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by title, artist, or album..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    className="pl-10 bg-background/50"
                    data-testid="search-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { fetchSongs(); fetchStats(); }}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                  {stats.total > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-500 hover:text-red-600"
                      onClick={() => setShowClearConfirm(true)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear All
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Songs List */}
          <Card className="glass-card border-glass-border">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Songs ({total.toLocaleString()})</span>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2 text-sm font-normal">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span>Page {page} of {totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-neon-purple" />
                </div>
              ) : songs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Music className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No songs in your library</p>
                  <p className="text-sm mt-2">Upload your Virtual DJ database or add songs manually</p>
                  <Button 
                    onClick={() => setShowUploadDialog(true)} 
                    className="mt-4 bg-neon-purple hover:bg-neon-purple/80"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Library
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {songs.map(song => (
                    <div
                      key={song.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-glass-border hover:border-neon-purple/50 transition-colors group"
                      data-testid={`song-${song.id}`}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <Music className="w-8 h-8 text-neon-purple/50 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{song.title}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {song.artist || 'Unknown Artist'}
                            {song.album && ` • ${song.album}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {song.duration_formatted && (
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {song.duration_formatted}
                          </span>
                        )}
                        <Badge className={`${getSourceBadgeColor(song.source)} text-white text-xs`}>
                          {song.source === 'virtual_dj' ? 'VDJ' : song.source?.toUpperCase()}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600"
                          onClick={() => { setSongToDelete(song); setShowDeleteConfirm(true); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="glass-card border-glass-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-neon-purple" />
              Upload Music Library
            </DialogTitle>
            <DialogDescription>
              Import your music collection from Virtual DJ, CSV, or M3U files
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Upload Mode */}
            <div className="space-y-2">
              <Label>Import Mode</Label>
              <Select value={uploadMode} onValueChange={setUploadMode}>
                <SelectTrigger data-testid="upload-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="append">
                    <div className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Append to existing library
                    </div>
                  </SelectItem>
                  <SelectItem value="replace">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4" />
                      Replace entire library
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {uploadMode === 'append' 
                  ? 'New songs will be added, duplicates will be skipped' 
                  : 'Warning: This will delete all existing songs first'}
              </p>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Select File</Label>
              <div className="border-2 border-dashed border-glass-border rounded-lg p-6 text-center hover:border-neon-purple/50 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xml,.csv,.m3u,.m3u8"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="file-input"
                />
                <FileMusic className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-neon-purple hover:bg-neon-purple/80"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Choose File
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-3">
                  Supported: Virtual DJ XML, CSV, M3U/M3U8 (Large files supported up to 100MB+)
                </p>
              </div>
              
              {/* Upload Progress */}
              {uploading && uploadProgress > 0 && !processingStatus && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                </div>
              )}
              
              {/* Processing Status */}
              {processingStatus && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-neon-purple" />
                    <span className="font-medium text-neon-purple">
                      {processingStatus.status === 'assembling' && 'Reassembling file...'}
                      {processingStatus.status === 'parsing' && 'Parsing music file...'}
                      {processingStatus.status === 'importing' && 'Importing songs...'}
                      {processingStatus.status === 'processing' && 'Processing...'}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {processingStatus.progress_message || 'Please wait, this may take a few minutes for large libraries...'}
                  </p>
                  {processingStatus.status === 'importing' && processingStatus.imported_count > 0 && (
                    <div className="text-sm">
                      <Progress 
                        value={Math.round((processingStatus.imported_count / (processingStatus.total_songs || 1)) * 100)} 
                        className="h-2 mt-2" 
                      />
                      <p className="mt-1 text-muted-foreground">
                        {processingStatus.imported_count.toLocaleString()} / {(processingStatus.total_songs || 0).toLocaleString()} songs
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Upload Result */}
            {uploadResult && !uploadResult.error && (
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                <div className="flex items-center gap-2 text-green-500 mb-2">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Import Complete!</span>
                </div>
                <div className="text-sm space-y-1">
                  <p>Songs imported: <span className="font-medium">{uploadResult.imported}</span></p>
                  <p>Duplicates skipped: <span className="font-medium">{uploadResult.duplicates_skipped}</span></p>
                  <p>Total in library: <span className="font-medium">{uploadResult.total_in_library}</span></p>
                </div>
              </div>
            )}
            
            {/* Upload Error */}
            {uploadResult && uploadResult.error && (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="flex items-center gap-2 text-red-500 mb-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">Import Failed</span>
                </div>
                <p className="text-sm text-red-300">{uploadResult.error}</p>
                <p className="text-xs text-muted-foreground mt-2">Please check the file format and try again.</p>
              </div>
            )}

            {/* Supported Formats Info */}
            <div className="p-3 rounded-lg bg-background/50 text-sm">
              <p className="font-medium mb-2">Supported Formats:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• <span className="text-purple-400">Virtual DJ XML</span> - Export from VDJ database</li>
                <li>• <span className="text-blue-400">CSV</span> - With columns: Title, Artist, Duration</li>
                <li>• <span className="text-green-400">M3U/M3U8</span> - Standard playlist files</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Song Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="glass-card border-glass-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-neon-blue" />
              Add Song Manually
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="Song title"
                value={newSong.title}
                onChange={(e) => setNewSong({ ...newSong, title: e.target.value })}
                className="bg-background/50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="artist">Artist</Label>
              <Input
                id="artist"
                placeholder="Artist name"
                value={newSong.artist}
                onChange={(e) => setNewSong({ ...newSong, artist: e.target.value })}
                className="bg-background/50"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration">Duration</Label>
                <Input
                  id="duration"
                  placeholder="e.g., 3:45"
                  value={newSong.duration}
                  onChange={(e) => setNewSong({ ...newSong, duration: e.target.value })}
                  className="bg-background/50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="genre">Genre</Label>
                <Input
                  id="genre"
                  placeholder="e.g., Pop"
                  value={newSong.genre}
                  onChange={(e) => setNewSong({ ...newSong, genre: e.target.value })}
                  className="bg-background/50"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="album">Album</Label>
              <Input
                id="album"
                placeholder="Album name"
                value={newSong.album}
                onChange={(e) => setNewSong({ ...newSong, album: e.target.value })}
                className="bg-background/50"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddSong} className="bg-neon-blue hover:bg-neon-blue/80">
              Add Song
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Song Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="glass-card border-glass-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Trash2 className="w-5 h-5" />
              Delete Song
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to remove "{songToDelete?.title}" by {songToDelete?.artist || 'Unknown Artist'} from your library?
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteSong}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clear Library Confirmation */}
      <Dialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <DialogContent className="glass-card border-glass-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" />
              Clear Entire Library
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            This will permanently delete all {stats.total.toLocaleString()} songs from your music library. This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowClearConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleClearLibrary}>
              Clear All Songs
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
